import { useCallback, useEffect, useState, useMemo, Key } from 'react'
import { useSelector } from 'react-redux'
import { Table } from '../../../../components/Table'
import { PaymentActions } from '../../PaymentActions/PaymentActions'
import { isTableHasParams } from '../../../../helper/common'
import { useFinancialsRebatesContext } from '../../Providers/FinancialsRebatesProvider'
import { getUser } from '../../../../redux/store/user/getters'
import { useFinancialsAbility } from '../../Providers/FinancialsAbilityProvider'
import { useFinancialsContext } from '../../Providers/FinancialsProvider'
import { columns } from './constants'
import { getDisabledStatuses } from '../../utils'
import { ACTIONS, SUBJECTS, contractSubject } from '../../Abilities'
import {
  TRebatesTableFilters,
  TRebatesTableFunc,
  TRebatesTableType
} from './types'
import { FINANCIAL_STATUS, PAYMENT_TYPES } from '../../types'
import { ConfirmRebatesPaymentPopup } from 'features/Financials/PaymentActions/RebatesPaymentPopups/ConfirmRebatesPaymentPopup'
import { InitiateRebatesPaymentPopup } from 'features/Financials/PaymentActions/RebatesPaymentPopups/InitiateRebatesPaymentPopup'
import { CompleteRebatesPopup } from 'features/Financials/PaymentActions/CompleteRebatesPopup/CompleteRebatesPopup'

export const RebatesTable = () => {
  const [selectedRebatesRowData, setSelectedRebatesRowData] =
    useState<TRebatesTableType | null>(null)
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([])
  const [filters, setFilters] = useState<TRebatesTableFilters>({})
  const {
    actions: { generateAndDownloadRebateDocument }
  } = useFinancialsContext()
  const user = useSelector(getUser)
  const {
    state: { totalNumber, tableData, sortParams, firstLoad, period, pageInfo },
    actions: { getTableData, updateRebatesData, setPageInfo }
  } = useFinancialsRebatesContext()
  const ability = useFinancialsAbility()

  const canInitiateRebatesPayment = useMemo(
    () =>
      !!user.vendor && ability.can(ACTIONS.INITIATE, SUBJECTS.REBATES_PAYMENT),
    [ability, user.vendor]
  )

  const canCompleteRebatesPayment = useMemo(
    () => ability.can(ACTIONS.INITIATE, SUBJECTS.COMPLETE_REBATES_PAYMENT),
    [ability]
  )

  const canSeeHSName = useMemo(
    () =>
      ability.can(
        ACTIONS.VIEW,
        contractSubject(SUBJECTS.REBATES_HS_NAME, user)
      ),
    [ability, user]
  )

  const canSeeInvoices = useMemo(
    () => ability.can(ACTIONS.VIEW, SUBJECTS.REBATES_INVOICES),
    [ability]
  )

  useEffect(() => {
    getTableData({ ...sortParams, page: pageInfo })
    setSelectedRowKeys([])
    setSelectedRebatesRowData(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period])

  const handleTableChange = (pagination, _filters, sorter) => {
    const page = {
      pageNumber: pagination.current,
      pageSize: pagination.pageSize
    }
    getTableData({
      sortField: sorter.field,
      sortOrder: sorter.order,
      filters,
      page
    })
    setPageInfo(page)
  }

  const handleChangePageSize = (pageSize) => {
    const page = {
      pageNumber: 1,
      pageSize
    }
    getTableData({
      ...sortParams,
      filters,
      page
    })
    setPageInfo(page)
  }

  const handleSelectRow = (
    selectedRowKeys: Key[],
    selectedRows: TRebatesTableType[]
  ) => {
    setSelectedRowKeys(selectedRowKeys)
    setSelectedRebatesRowData(selectedRows[0] ?? null)
  }

  const applyFilters: TRebatesTableFunc = (field, appliedFilters) => {
    const newFilters = { ...filters, [field]: appliedFilters }
    const newPageInfo = { ...pageInfo, pageNumber: 1 }
    setFilters(newFilters)
    setPageInfo(newPageInfo)
    getTableData({ ...sortParams, filters: newFilters, page: newPageInfo })
  }

  const onResetSelection = useCallback(() => {
    setSelectedRebatesRowData(null)
    setSelectedRowKeys([])
  }, [])

  const updateTableData = async () => {
    try {
      await updateRebatesData()
      onResetSelection()
    } finally {
    }
  }

  return (
    <div>
      <PaymentActions
        type={PAYMENT_TYPES.REBATES}
        totalNumber={totalNumber}
        pageInfo={pageInfo}
        selectedRowData={null}
        selectedLength={selectedRowKeys.length}
        updateTableData={updateTableData}
        onResetSelection={onResetSelection}
        selectedRebatesRowData={selectedRebatesRowData}
      />
      {firstLoad ? null : (
        <Table<TRebatesTableType>
          dataSource={tableData}
          columns={columns({
            applyFilters,
            filters,
            user,
            generateAndDownloadRebateDocument,
            canInitiateRebatesPayment,
            canCompleteRebatesPayment,
            canSeeHSName,
            canSeeInvoices
          })}
          className="financials-table"
          onChange={handleTableChange}
          hasSearchOrFilters={isTableHasParams<TRebatesTableFilters>(
            '',
            filters
          )}
          onChangePage={handleChangePageSize}
          pageSize={pageInfo.pageSize}
          pagination={{
            pageSize: pageInfo.pageSize,
            current: pageInfo.pageNumber,
            total: totalNumber
          }}
          rowSelection={
            canInitiateRebatesPayment || canCompleteRebatesPayment
              ? {
                  selectedRowKeys,
                  onChange: handleSelectRow,
                  getCheckboxProps: (record) => ({
                    disabled: canInitiateRebatesPayment
                      ? getDisabledStatuses(
                          user.role,
                          record.due_date_status
                        ).includes(record.status)
                      : canCompleteRebatesPayment
                      ? record.status !== FINANCIAL_STATUS.PARTIALLY_PAID
                      : true
                  })
                }
              : undefined
          }
        />
      )}
      <ConfirmRebatesPaymentPopup />
      <InitiateRebatesPaymentPopup onResetSelection={onResetSelection} />
      <CompleteRebatesPopup onResetSelection={onResetSelection} />
    </div>
  )
}
